import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Book,
  PencilRuler,
  HardHat,
  Building,
  Download,
  Edit,
  Save,
  Plus,
  Trash2,
  Lock
} from 'lucide-react';
import { collection, getDocs, updateDoc, deleteDoc, doc, query, where, serverTimestamp, addDoc, getDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, getDownloadURL, getBytes } from 'firebase/storage';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getAccountById } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import AddButton from '../components/AddButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import StartSidebar from '../components/StartSidebar';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const StartSOPs = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('design');
  const [selectedSOP, setSelectedSOP] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSOP, setEditedSOP] = useState(null);
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [sops, setSops] = useState({
    design: [],
    construction: [],
    office: []
  });
  const [userCustomizations, setUserCustomizations] = useState({});
  const [unlockedSOPs, setUnlockedSOPs] = useState(null); // null means not set, array means explicitly set
  const modalRef = useRef(null);


  const tabs = [
    { id: 'design', label: 'Design', icon: PencilRuler },
    { id: 'construction', label: 'Construction', icon: HardHat },
    { id: 'office', label: 'Office', icon: Building }
  ];

  // Fetch SOPs from Firestore and user customizations
  useEffect(() => {
    if (currentUser) {
      fetchSOPs();
      fetchUserCustomizations();
    }
  }, [currentUser]);

  const fetchSOPs = async () => {
    try {
      // Get user's accountId from their user document
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      let unlockedSOPsList = null; // null means not set, array means explicitly set
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
        
        // Fetch account data to get unlockedSOPs
        if (accountId) {
          try {
            const accountData = await getAccountById(accountId);
            const isFullPortalClient =
              accountData &&
              (accountData.program?.portalAccess === true ||
                accountData.program?.clientStatus === 'Active' ||
                accountData.stage === 'Portal Client' ||
                accountData.substatus === 'Portal Signup Completed');
            // Lead flow uses unlockedSOPs as a subset; promoted / active portal accounts always get every SOP
            if (isFullPortalClient) {
              unlockedSOPsList = null;
            } else if (accountData && accountData.unlockedSOPs !== undefined) {
              const list = Array.isArray(accountData.unlockedSOPs) ? accountData.unlockedSOPs : [];
              // Empty list from invite setup means "no IDs matched" — show all SOPs for leads
              unlockedSOPsList =
                list.length === 0 && accountData.freePage === 'operations/sops' ? null : list;
            }
          } catch (error) {
            console.error('Error fetching account data:', error);
            // Continue without unlockedSOPs filter if account fetch fails
          }
        }
      }

      // Store unlockedSOPs in state for checking during render
      setUnlockedSOPs(unlockedSOPsList);

      // Fetch all SOPs from templates
      const templateQuerySnapshot = await getDocs(collection(db, 'sopTemplates'));
      const templateSOPs = templateQuerySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch account-specific SOPs if accountId exists
      let accountSOPs = [];
      if (accountId) {
        try {
          const accountSOPsQuerySnapshot = await getDocs(collection(db, 'accounts', accountId, 'sops'));
          accountSOPs = accountSOPsQuerySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.error('Error fetching account-specific SOPs:', error);
          // Continue with just template SOPs if account SOPs fail to load
        }
      }

      // Combine template SOPs and account-specific SOPs - show ALL SOPs
      const allSOPs = [...templateSOPs, ...accountSOPs];
      
      // Group SOPs by department
      const groupedSOPs = {
        design: allSOPs.filter(sop => sop.department === 'design'),
        construction: allSOPs.filter(sop => sop.department === 'construction'),
        office: allSOPs.filter(sop => sop.department === 'office')
      };
      
      setSops(groupedSOPs);
    } catch (error) {
      console.error('Error fetching SOPs:', error);
    }
  };

  const fetchUserCustomizations = async () => {
    try {
      // Get all user customizations
      const customizationsQuery = query(
        collection(db, 'userSOPCustomizations'),
        where('userId', '==', currentUser.uid)
      );
      const querySnapshot = await getDocs(customizationsQuery);
      const customizations = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.templateId) {
          customizations[data.templateId] = {
            id: doc.id,
            ...data
          };
        }
      });
      
      setUserCustomizations(customizations);
    } catch (error) {
      console.error('Error fetching user customizations:', error);
    }
  };

  const handleSOPClick = useCallback((sop, event) => {
    // Prevent event propagation to avoid interference from other handlers
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Validate SOP data
    if (!sop || !sop.id) {
      console.error('Invalid SOP data:', sop);
      alert('Error: Invalid SOP data. Please try again.');
      return;
    }
    
    try {
      // Check if user has customizations
      const userCustomization = userCustomizations[sop.id];
      
      // Start with the template SOP data and ensure all required fields exist
      let displaySOP = {
        id: sop.id,
        title: sop.title || 'Untitled SOP',
        description: sop.description || '',
        department: sop.department || activeTab,
        steps: sop.steps || [],
        ...sop
      };
      
      // If user has customizations, merge them with the template
      if (userCustomization) {
        displaySOP = {
          ...displaySOP,
          title: userCustomization.title || sop.title || 'Untitled SOP',
          description: userCustomization.description || sop.description || '',
          steps: userCustomization.steps || sop.steps || []
        };
      }
      
      // Store the original template ID for saving customizations
      displaySOP.templateId = sop.id;
      displaySOP.hasCustomizations = !!userCustomization;
      
      setSelectedSOP(displaySOP);
      setEditedSOP(displaySOP);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error handling SOP click:', error);
      alert('Error opening SOP. Please try again.');
    }
  }, [userCustomizations, activeTab]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      const customizationData = {
        userId: currentUser.uid,
        templateId: selectedSOP.templateId,
        title: editedSOP.title || '',
        description: editedSOP.description || '',
        steps: editedSOP.steps || [],
        department: editedSOP.department || '',
        updatedAt: serverTimestamp()
      };

      if (selectedSOP.hasCustomizations) {
        // Update existing customization
        const customizationId = userCustomizations[selectedSOP.templateId].id;
        const docRef = doc(db, 'userSOPCustomizations', customizationId);
        await updateDoc(docRef, customizationData);
      } else {
        // Create new customization
        const docRef = await addDoc(collection(db, 'userSOPCustomizations'), {
          ...customizationData,
          createdAt: serverTimestamp()
        });
        
        // Update local customizations state
        setUserCustomizations(prev => ({
          ...prev,
          [selectedSOP.templateId]: {
            id: docRef.id,
            ...customizationData
          }
        }));
      }
      
      // Update local state to show the customized version
      setSelectedSOP({
        ...editedSOP,
        hasCustomizations: true
      });
      
      // Update the SOPs display to show customized version
      setSops(prev => ({
        ...prev,
        [editedSOP.department]: prev[editedSOP.department].map(sop => 
          sop.id === selectedSOP.templateId ? {
            ...sop,
            title: editedSOP.title,
            description: editedSOP.description,
            steps: editedSOP.steps
          } : sop
        )
      }));
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving SOP customization:', error);
      alert('Error saving SOP customization. Please try again.');
    }
  };

  const handleCancel = () => {
    setEditedSOP({ ...selectedSOP });
    setIsEditing(false);
  };

  const handleResetToTemplate = async () => {
    try {
      if (selectedSOP.hasCustomizations) {
        // Delete the user customization
        const customizationId = userCustomizations[selectedSOP.templateId].id;
        await deleteDoc(doc(db, 'userSOPCustomizations', customizationId));
        
        // Remove from local state
        setUserCustomizations(prev => {
          const newCustomizations = { ...prev };
          delete newCustomizations[selectedSOP.templateId];
          return newCustomizations;
        });
        
        // Reset the SOP to template version
        const templateSOP = sops[selectedSOP.department].find(sop => sop.id === selectedSOP.templateId);
        const resetSOP = { ...templateSOP, templateId: templateSOP.id, hasCustomizations: false };
        
        setSelectedSOP(resetSOP);
        setEditedSOP(resetSOP);
        
        // Update SOPs display to show template version
        setSops(prev => ({
          ...prev,
          [selectedSOP.department]: prev[selectedSOP.department].map(sop => 
            sop.id === selectedSOP.templateId ? {
              ...templateSOP,
              title: templateSOP.title,
              description: templateSOP.description,
              steps: templateSOP.steps
            } : sop
          )
        }));
      }
    } catch (error) {
      console.error('Error resetting to template:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!modalRef.current) return;

    try {
      // Fetch user's company logo, colors, and company name
      let companyLogo = null;
      let companyName = '';
      let primaryColor = '#dc2626';
      let secondaryColor = '#1f2937';
      
      if (currentUser) {
        try {
          // Try to get from users collection first (prioritize client profile logo)
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.companyLogo) {
              companyLogo = userData.companyLogo;
            }
            if (userData.company) {
              companyName = userData.company;
            }
            if (userData.companyColors) {
              primaryColor = userData.companyColors.primary || primaryColor;
              secondaryColor = userData.companyColors.secondary || secondaryColor;
            }
          }
          
          // Also check internal_users collection if companyLogo not found in users
          // This ensures we check both collections for the profile logo
          if (!companyLogo) {
            const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
            if (internalUserDoc.exists()) {
              const userData = internalUserDoc.data();
              if (userData.companyLogo) {
                companyLogo = userData.companyLogo;
              }
              // Only set company name and colors if not already set from users collection
              if (!companyName && userData.company) {
                companyName = userData.company;
              }
              if (userData.companyColors) {
                primaryColor = userData.companyColors.primary || primaryColor;
                secondaryColor = userData.companyColors.secondary || secondaryColor;
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user settings:', error);
        }
      }
      
      // Helper function to load logo as data URL, avoiding CORS issues
      const loadLogoAsDataUrl = async (url) => {
        try {
          // Check if it's a Firebase Storage URL
          const isFirebaseStorageURL = url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app');
          
          // For Firebase Storage URLs, ONLY use the SDK (never use fetch due to CORS)
          if (isFirebaseStorageURL && currentUser) {
            try {
              // Extract the storage path from the URL
              // URL format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token=...
              const urlMatch = url.match(/\/o\/([^?]+)/);
              if (urlMatch) {
                const encodedPath = urlMatch[1];
                const storagePath = decodeURIComponent(encodedPath);
                
                console.log('Attempting to load company logo from Firebase Storage:', storagePath);
                
                // Use Firebase Storage SDK to download directly (bypasses CORS)
                const storageRef = ref(storage, storagePath);
                const bytes = await getBytes(storageRef);
                
                // Determine blob type from file extension or bytes
                let blobType = 'image/png'; // default
                if (storagePath.endsWith('.svg')) {
                  blobType = 'image/svg+xml';
                } else if (storagePath.endsWith('.jpg') || storagePath.endsWith('.jpeg')) {
                  blobType = 'image/jpeg';
                } else if (storagePath.endsWith('.png')) {
                  blobType = 'image/png';
                } else if (storagePath.endsWith('.gif')) {
                  blobType = 'image/gif';
                } else if (storagePath.endsWith('.webp')) {
                  blobType = 'image/webp';
                }
                
                // Convert bytes to blob with proper type
                const blob = new Blob([bytes], { type: blobType });
                
                // Convert blob to data URL
                return new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const dataUrl = reader.result;
                    // If it's SVG, convert to PNG via canvas
                    if (storagePath.endsWith('.svg') || blob.type === 'image/svg+xml') {
                      const img = new Image();
                      img.onload = () => {
                        try {
                          const canvas = document.createElement('canvas');
                          canvas.width = img.naturalWidth || 200;
                          canvas.height = img.naturalHeight || 50;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0);
                          resolve(canvas.toDataURL('image/png'));
                        } catch (error) {
                          resolve(dataUrl);
                        }
                      };
                      img.onerror = () => resolve(dataUrl);
                      img.src = dataUrl;
                    } else {
                      resolve(dataUrl);
                    }
                  };
                  reader.onerror = () => resolve(null);
                  reader.readAsDataURL(blob);
                });
              } else {
                console.warn('Could not extract storage path from Firebase Storage URL');
                return null;
              }
            } catch (error) {
              console.warn('Error loading logo from Firebase Storage SDK:', error);
              // For Firebase Storage URLs, don't fall back to fetch (will cause CORS error)
              return null;
            }
          }
          
          // For non-Firebase Storage URLs (like local files), use fetch
          if (url.startsWith('http') || url.startsWith('/')) {
            try {
              const response = await fetch(url);
              if (!response.ok) throw new Error('Failed to fetch');
              const blob = await response.blob();
              
              return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const dataUrl = reader.result;
                  // If it's SVG, convert to PNG via canvas
                  if (url.endsWith('.svg') || blob.type === 'image/svg+xml') {
                    const img = new Image();
                    img.onload = () => {
                      try {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth || 200;
                        canvas.height = img.naturalHeight || 50;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        resolve(canvas.toDataURL('image/png'));
                      } catch (error) {
                        resolve(dataUrl);
                      }
                    };
                    img.onerror = () => resolve(dataUrl);
                    img.src = dataUrl;
                  } else {
                    resolve(dataUrl);
                  }
                };
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
              });
            } catch (error) {
              console.warn('Error fetching logo via fetch:', error);
              return null;
            }
          }
          
          // If all else fails, return null
          return null;
        } catch (error) {
          console.warn('Error loading logo:', error);
          return null;
        }
      };
      
      // Load logo as data URL (will be added directly to PDF pages, not in HTML)
      // Try company logo first, then fall back to BBA logo
      let logoDataUrl = null;
      let logoDimensions = { width: 200, height: 50 };
      
      console.log('Starting logo load, companyLogo:', companyLogo);
      
      // Try company logo first
      if (companyLogo) {
        console.log('Attempting to load company logo:', companyLogo);
        const logoLoadPromise = loadLogoAsDataUrl(companyLogo).catch((err) => {
          console.warn('Company logo load failed:', err);
          return null;
        });
        // Use longer timeout for Firebase Storage URLs (5 seconds)
        const isFirebaseStorageURL = companyLogo.includes('firebasestorage.googleapis.com') || companyLogo.includes('firebasestorage.app');
        const timeoutDuration = isFirebaseStorageURL ? 5000 : 2000; // 5 seconds for Firebase Storage, 2 seconds for others
        const timeoutPromise = new Promise((resolve) => setTimeout(() => {
          console.log('Company logo load timeout');
          resolve(null);
        }, timeoutDuration));
        logoDataUrl = await Promise.race([logoLoadPromise, timeoutPromise]);
        console.log('Company logo result:', logoDataUrl ? 'Success' : 'Failed');
      }
      
      // If company logo failed or doesn't exist, try BBA logo
      if (!logoDataUrl) {
        const bbaLogoUrl = '/bba-logo.png';
        console.log('Attempting to load BBA logo:', bbaLogoUrl);
        const logoLoadPromise = loadLogoAsDataUrl(bbaLogoUrl).catch((err) => {
          console.warn('BBA logo load failed:', err);
          return null;
        });
        const timeoutPromise = new Promise((resolve) => setTimeout(() => {
          console.log('BBA logo load timeout');
          resolve(null);
        }, 2000)); // 2 second timeout
        logoDataUrl = await Promise.race([logoLoadPromise, timeoutPromise]);
        console.log('BBA logo result:', logoDataUrl ? 'Success' : 'Failed');
      }
      
      console.log('Final logoDataUrl:', logoDataUrl ? 'Has logo' : 'No logo');
      
      // Get logo dimensions if we have a logo
      if (logoDataUrl) {
        console.log('Getting logo dimensions');
        const img = new Image();
        await Promise.race([
          new Promise((resolve) => {
            img.onload = () => {
              logoDimensions = { width: img.naturalWidth || img.width || 200, height: img.naturalHeight || img.height || 50 };
              console.log('Logo dimensions:', logoDimensions);
              resolve();
            };
            img.onerror = () => {
              console.warn('Error loading logo image for dimensions');
              resolve();
            };
            img.src = logoDataUrl;
          }),
          new Promise((resolve) => setTimeout(() => {
            console.log('Logo dimensions timeout');
            resolve();
          }, 2000)) // 2 second timeout
        ]);
      }
      
      // Helper function to safely escape HTML and handle text
      const escapeHtml = (text) => {
        if (text === null || text === undefined) return '';
        const textStr = String(text);
        return textStr
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      };
      
      // Create the PDF content container
      const sop = selectedSOP;
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.width = '800px';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.color = 'black';
      tempContainer.style.padding = '40px';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '12px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.wordWrap = 'break-word';
      tempContainer.style.overflowWrap = 'break-word';
      
      // Build HTML content (logo will be added directly to PDF pages using jsPDF to avoid CORS)
      tempContainer.innerHTML = `
        <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="font-size: 28px; font-weight: bold; color: black; margin-bottom: 10px; word-wrap: break-word;">${escapeHtml(sop.title)}</h1>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 22px; font-weight: bold; color: ${secondaryColor}; border-bottom: 2px solid ${primaryColor}; padding-bottom: 5px; margin-bottom: 15px;">Description</h2>
          <p style="margin: 0; word-wrap: break-word; line-height: 1.6; font-size: 14px; color: black;">${escapeHtml(sop.description || 'No description provided')}</p>
        </div>
        
        <div style="margin-bottom: 25px;">
          <h2 style="font-size: 22px; font-weight: bold; color: ${secondaryColor}; border-bottom: 2px solid ${primaryColor}; padding-bottom: 5px; margin-bottom: 15px;">Steps</h2>
          ${(sop.steps || []).map((step, index) => `
            <div class="step-container" data-step-index="${index}" style="margin-bottom: 0; padding: 15px; padding-top: ${index === 0 ? '15px' : '5px'}; page-break-inside: avoid; break-inside: avoid; orphans: 3; widows: 3;">
              <h3 style="font-size: 18px; font-weight: bold; color: black; margin: 0 0 8px 0; word-wrap: break-word;">${index + 1}. ${escapeHtml(step.title || `Step ${index + 1}`)}</h3>
              ${step.description && step.description.trim() !== '' ? 
                `<p style="margin: 0 0 12px 0; color: black; word-wrap: break-word; line-height: 1.6; font-size: 14px;">${escapeHtml(step.description)}</p>` : ''
              }
              ${(step.descriptionItems || []).length > 0 ? `
                <ul style="margin: 0; padding-left: 20px; list-style-type: disc;">
                  ${(step.descriptionItems || []).map((item, itemIndex) => {
                    const itemText = typeof item === 'string' ? item : (item.text || '');
                    const subItems = typeof item === 'string' ? [] : (item.subItems || []);
                    
                    return `
                      <li style="margin-bottom: 8px; word-wrap: break-word; color: black; font-size: 14px;">
                        ${escapeHtml(itemText || 'Empty item')}
                        ${subItems.length > 0 ? `
                          <ul style="margin: 8px 0 0 0; padding-left: 20px; list-style-type: circle;">
                            ${subItems.map((subItem, subItemIndex) => {
                              const subItemText = typeof subItem === 'string' ? subItem : (subItem?.text || String(subItem || 'Empty sub-item'));
                              return `
                              <li style="margin-bottom: 4px; color: black; font-size: 13px; word-wrap: break-word;">
                                ${escapeHtml(subItemText)}
                              </li>
                            `;
                            }).join('')}
                          </ul>
                        ` : ''}
                      </li>
                    `;
                  }).join('')}
                </ul>
              ` : ''}
            </div>
          `).join('')}
        </div>
        
      `;
      
      document.body.appendChild(tempContainer);
      
      // Wait for all images (including logo) to load
      const images = tempContainer.querySelectorAll('img');
      await Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = resolve; // Continue even if image fails
            setTimeout(resolve, 3000); // Timeout after 3 seconds
          });
        })
      );
      
      // Wait for DOM to render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Convert to canvas with better page break handling
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false,
        removeContainer: false,
        onclone: (clonedDoc) => {
          // Ensure page-break-inside: avoid is applied and add additional break controls
          const clonedContainer = clonedDoc.querySelector('div');
          if (clonedContainer) {
            const stepDivs = clonedContainer.querySelectorAll('.step-container');
            stepDivs.forEach(div => {
              div.style.pageBreakInside = 'avoid';
              div.style.breakInside = 'avoid';
              div.style.orphans = '3';
              div.style.widows = '3';
              // Also prevent breaking the step title
              const stepTitle = div.querySelector('h3');
              if (stepTitle) {
                stepTitle.style.pageBreakAfter = 'avoid';
                stepTitle.style.breakAfter = 'avoid';
              }
            });
          }
        }
      });
      
      // Remove temporary container
      document.body.removeChild(tempContainer);
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const pageMargin = 10; // 10mm margin
      const logoHeight = logoDataUrl ? 20 : 0; // Logo height in mm (reduced from 30 to fit better)
      const logoBottomMargin = logoDataUrl ? 5 : 0; // Space below logo
      const headerHeight = logoDataUrl ? logoHeight + logoBottomMargin : 0; // Total space for logo
      const footerHeight = 15; // Space for footer (company name + page number)
      const contentAreaHeight = pdfHeight - pageMargin - headerHeight - pageMargin - footerHeight;
      
      // Calculate image dimensions
      const imgWidth = pdfWidth - (pageMargin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Function to add header (logo) and footer (company name + page number) to each page
      const addPageHeaderFooter = (pageNum, totalPages) => {
        // Add logo centered at top on every page
        if (logoDataUrl) {
          try {
            console.log(`Adding logo to page ${pageNum}, logoDataUrl exists:`, !!logoDataUrl);
            const maxLogoHeight = 20; // 20mm max height (reduced to fit better in header)
            const maxLogoWidth = 70; // 70mm max width (reduced proportionally)
            const logoAspectRatio = logoDimensions.width / logoDimensions.height;
            
            let calculatedLogoHeight = maxLogoHeight;
            let calculatedLogoWidth = calculatedLogoHeight * logoAspectRatio;
            
            // If width exceeds max, scale down
            if (calculatedLogoWidth > maxLogoWidth) {
              calculatedLogoWidth = maxLogoWidth;
              calculatedLogoHeight = calculatedLogoWidth / logoAspectRatio;
            }
            
            // Center the logo horizontally
            const logoX = (pdfWidth - calculatedLogoWidth) / 2;
            
            console.log(`Adding logo with dimensions: ${calculatedLogoWidth}mm x ${calculatedLogoHeight}mm`);
            pdf.addImage(logoDataUrl, 'PNG', logoX, pageMargin, calculatedLogoWidth, calculatedLogoHeight);
            console.log('Logo added successfully to page', pageNum);
          } catch (error) {
            console.error('Error adding logo to PDF:', error);
          }
        } else {
          console.log(`No logo to add to page ${pageNum}`);
        }
        
        // Add company name at bottom left
        if (companyName) {
          pdf.setFontSize(10);
          pdf.setTextColor(100, 100, 100);
          pdf.text(companyName, pageMargin, pdfHeight - pageMargin);
        }
        
        // Add date in the middle of footer
        const currentDate = new Date().toLocaleDateString();
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const dateWidth = pdf.getTextWidth(currentDate);
        pdf.text(currentDate, (pdfWidth - dateWidth) / 2, pdfHeight - pageMargin);
        
        // Add page number at bottom right
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${pageNum} of ${totalPages}`;
        const textWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdfWidth - pageMargin - textWidth, pdfHeight - pageMargin);
      };
      
      // Split content across pages properly - use conservative approach to never split text
      // We'll use a larger buffer and be very careful about line boundaries
      const baseContentAreaHeight = contentAreaHeight - 30; // 30mm buffer to be safe
      
      // Estimate line height more accurately
      // Font size is 14px, line height is 1.6, so approximately 22-24px per line
      // But we'll be more conservative and use a larger value
      const estimatedLineHeight = 28; // pixels - slightly larger to be safe
      const estimatedLineHeightInMM = (estimatedLineHeight / canvas.height) * imgHeight; // Convert to mm
      
      // Function to round down to nearest line boundary (for start positions)
      const roundDownToLineBoundary = (position) => {
        if (position <= 0) return 0;
        const positionInPixels = (position / imgHeight) * canvas.height;
        const linesFromTop = Math.floor(positionInPixels / estimatedLineHeight);
        const roundedPixels = linesFromTop * estimatedLineHeight;
        return (roundedPixels / canvas.height) * imgHeight;
      };
      
      // Function to round up to nearest line boundary (for end positions)
      const roundUpToLineBoundary = (position) => {
        if (position >= imgHeight) return imgHeight;
        const positionInPixels = (position / imgHeight) * canvas.height;
        const linesFromTop = Math.ceil(positionInPixels / estimatedLineHeight);
        const roundedPixels = linesFromTop * estimatedLineHeight;
        return Math.min((roundedPixels / canvas.height) * imgHeight, imgHeight);
      };
      
      // Track where each page ends to prevent overlaps
      let currentY = 0;
      const pages = [];
      
      // Pre-calculate all page boundaries to ensure no text splitting
      while (currentY < imgHeight) {
        const remainingHeight = imgHeight - currentY;
        
        if (remainingHeight <= baseContentAreaHeight) {
          // Last page - take all remaining content
          pages.push({
            startY: currentY,
            endY: imgHeight,
            height: imgHeight - currentY
          });
          break;
        }
        
        // Calculate ideal end position
        const idealEndY = currentY + baseContentAreaHeight;
        
        // Round down the end position to ensure we don't cut text
        // We want to end at a complete line, so round down, then add one full line
        const roundedDownEnd = roundDownToLineBoundary(idealEndY);
        const safeEndY = Math.min(roundedDownEnd + estimatedLineHeightInMM, imgHeight);
        
        // Ensure we have at least some content on this page
        const pageEndY = Math.max(safeEndY, currentY + estimatedLineHeightInMM * 3); // At least 3 lines
        const finalEndY = Math.min(pageEndY, imgHeight);
        
        pages.push({
          startY: currentY,
          endY: finalEndY,
          height: finalEndY - currentY
        });
        
        // Next page starts exactly where this one ends
        currentY = finalEndY;
      }
      
      const totalPages = pages.length;
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) {
          pdf.addPage();
        }
        
        // Add header and footer first
        addPageHeaderFooter(pageNum, totalPages);
        
        const page = pages[pageNum - 1];
        const sourceY = page.startY;
        const pageContentHeight = page.height;
        
        const destY = pageMargin + headerHeight; // Start content below the logo
        
        // Calculate the source rectangle in the original canvas
        const sourceYInCanvas = (sourceY / imgHeight) * canvas.height;
        const sourceHeightInCanvas = (pageContentHeight / imgHeight) * canvas.height;
        
        // Create a temporary canvas for this page's slice
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeightInCanvas;
        const pageCtx = pageCanvas.getContext('2d');
        
        // Draw the slice of the original canvas
        pageCtx.drawImage(
          canvas,
          0, sourceYInCanvas, // Source x, y
          canvas.width, sourceHeightInCanvas, // Source width, height
          0, 0, // Destination x, y
          canvas.width, sourceHeightInCanvas // Destination width, height
        );
        
        // Convert to image data
        const pageImgData = pageCanvas.toDataURL('image/png');
        
        // Add to PDF
        pdf.addImage(pageImgData, 'PNG', pageMargin, destY, imgWidth, pageContentHeight);
      }
      
      // Download the PDF
      const fileName = `${String(sop.title || 'sop').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_sop.pdf`;
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleNewTemplate = () => {
    const newTemplate = {
      id: Date.now(),
      title: 'New SOP',
      description: 'SOP description',
      department: activeTab,
      steps: []
    };
    setSelectedSOP(newTemplate);
    setEditedSOP(newTemplate);
    setIsNewTemplate(true);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseNewTemplate = () => {
    setIsModalOpen(false);
    setIsNewTemplate(false);
    setIsEditing(false);
    setSelectedSOP(null);
    setEditedSOP(null);
  };

  const handleDeleteNewTemplate = () => {
    setIsModalOpen(false);
    setIsNewTemplate(false);
    setIsEditing(false);
    setSelectedSOP(null);
    setEditedSOP(null);
  };

  const handleAddStep = () => {
    const newStep = {
      title: 'New Step',
      description: 'Step description',
      descriptionItems: []
    };
    setEditedSOP({
      ...editedSOP,
      steps: [...(editedSOP.steps || []), newStep]
    });
  };

  const handleUpdateStep = (stepIndex, field, value) => {
    const newSteps = [...(editedSOP.steps || [])];
    newSteps[stepIndex] = {
      ...newSteps[stepIndex],
      [field]: value
    };
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleDeleteStep = (stepIndex) => {
    const newSteps = (editedSOP.steps || []).filter((_, index) => index !== stepIndex);
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleAddDescriptionItem = (stepIndex) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (!newSteps[stepIndex].descriptionItems) {
      newSteps[stepIndex].descriptionItems = [];
    }
    newSteps[stepIndex].descriptionItems.push({
      text: '',
      subItems: []
    });
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleAddSubItem = (stepIndex, itemIndex) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (!newSteps[stepIndex].descriptionItems) {
      newSteps[stepIndex].descriptionItems = [];
    }
    if (!newSteps[stepIndex].descriptionItems[itemIndex].subItems) {
      newSteps[stepIndex].descriptionItems[itemIndex].subItems = [];
    }
    newSteps[stepIndex].descriptionItems[itemIndex].subItems.push('');
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleUpdateSubItem = (stepIndex, itemIndex, subItemIndex, value) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (!newSteps[stepIndex].descriptionItems[itemIndex].subItems) {
      newSteps[stepIndex].descriptionItems[itemIndex].subItems = [];
    }
    newSteps[stepIndex].descriptionItems[itemIndex].subItems[subItemIndex] = value;
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleDeleteSubItem = (stepIndex, itemIndex, subItemIndex) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (newSteps[stepIndex].descriptionItems[itemIndex].subItems) {
      newSteps[stepIndex].descriptionItems[itemIndex].subItems = 
        newSteps[stepIndex].descriptionItems[itemIndex].subItems.filter((_, i) => i !== subItemIndex);
    }
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleUpdateDescriptionItem = (stepIndex, itemIndex, value) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (!newSteps[stepIndex].descriptionItems) {
      newSteps[stepIndex].descriptionItems = [];
    }
    if (typeof newSteps[stepIndex].descriptionItems[itemIndex] === 'string') {
      newSteps[stepIndex].descriptionItems[itemIndex] = {
        text: value,
        subItems: []
      };
    } else {
      newSteps[stepIndex].descriptionItems[itemIndex] = {
        ...newSteps[stepIndex].descriptionItems[itemIndex],
        text: value
      };
    }
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const handleDeleteDescriptionItem = (stepIndex, itemIndex) => {
    const newSteps = [...(editedSOP.steps || [])];
    if (newSteps[stepIndex].descriptionItems) {
      newSteps[stepIndex].descriptionItems = newSteps[stepIndex].descriptionItems.filter((_, i) => i !== itemIndex);
    }
    setEditedSOP({
      ...editedSOP,
      steps: newSteps
    });
  };

  const renderStep = (step, index) => {
    if (!step) return null;
    
    return (
      <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
        <div className="flex items-start gap-2 sm:gap-4">
          <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center gap-2">
                  <input
                    type="text"
                    value={step.title || ''}
                    onChange={(e) => handleUpdateStep(index, 'title', e.target.value)}
                    className={`flex-1 min-w-0 border rounded px-2 py-1 text-xs sm:text-sm font-semibold ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                    placeholder="Step Title"
                  />
                  <button
                    onClick={() => handleDeleteStep(index)}
                    className="text-red-600 hover:text-red-700 flex-shrink-0"
                    title="Delete Step"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  value={step.description || ''}
                  onChange={(e) => handleUpdateStep(index, 'description', e.target.value)}
                  className={`w-full border rounded px-2 py-1 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                  placeholder="Step Description"
                />
                <div className="space-y-2">
                  {(step.descriptionItems || []).map((item, itemIndex) => {
                    const itemText = typeof item === 'string' ? item : (item.text ?? item.content ?? '');
                    const subItems = typeof item === 'string' ? [] : (item.subItems || []);
                    
                    return (
                      <div key={itemIndex} className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={itemText}
                            onChange={(e) => handleUpdateDescriptionItem(index, itemIndex, e.target.value)}
                            className={`flex-1 min-w-0 border rounded px-2 py-1 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                            placeholder="Description Item"
                          />
                          <button
                            onClick={() => handleAddSubItem(index, itemIndex)}
                            className="text-blue-600 hover:text-blue-700 flex-shrink-0"
                            title="Add Sub-Item"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDescriptionItem(index, itemIndex)}
                            className="text-red-600 hover:text-red-700 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Sub-items */}
                        {subItems.map((subItem, subItemIndex) => (
                          <div key={subItemIndex} className="flex gap-2 ml-2 sm:ml-4">
                            <input
                              type="text"
                              value={typeof subItem === 'string' ? subItem : (subItem?.text ?? subItem?.content ?? '')}
                              onChange={(e) => handleUpdateSubItem(index, itemIndex, subItemIndex, e.target.value)}
                              className={`flex-1 min-w-0 border rounded px-2 py-1 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
                              placeholder="Sub-Item"
                            />
                            <button
                              onClick={() => handleDeleteSubItem(index, itemIndex, subItemIndex)}
                              className="text-red-600 hover:text-red-700 flex-shrink-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                  <button
                    onClick={() => handleAddDescriptionItem(index)}
                    className="text-red-600 hover:text-red-700 flex items-center text-xs sm:text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                <h3 className={`text-base sm:text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} break-words`}>{step.title || 'Untitled Step'}</h3>
                {step.description && step.description.trim() !== '' && (
                  <p className={`text-sm sm:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} break-words`}>{step.description}</p>
                )}
                <ul className="list-disc list-inside space-y-1 text-sm sm:text-base">
                  {(step.descriptionItems || []).map((item, itemIndex) => {
                    // Ensure we have a valid item
                    if (!item) return null;
                    
                    const itemText = typeof item === 'string' ? item : (item.text ?? item.content ?? '');
                    const subItems = typeof item === 'string' ? [] : (Array.isArray(item.subItems) ? item.subItems : []);
                    
                    return (
                      <li key={itemIndex} className={`${darkMode ? 'text-gray-300' : 'text-gray-600'} break-words`}>
                        {itemText || 'Empty item'}
                        {subItems.length > 0 && (
                          <ul className="list-circle list-inside ml-2 sm:ml-4 mt-1 space-y-1">
                            {subItems.map((subItem, subItemIndex) => {
                              // Ensure subItem is valid
                              if (subItem === null || subItem === undefined) return null;
                              
                              const subItemText = typeof subItem === 'string' ? subItem : (subItem.text ?? subItem.content ?? 'Empty sub-item');
                              
                              return (
                                <li key={subItemIndex} className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} text-xs sm:text-sm break-words`}>
                                  {subItemText}
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEditableField = (field, title) => {
    const value = isEditing ? editedSOP[field] : selectedSOP[field];
    
    return (
      <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
        <h3 className={`text-base sm:text-lg font-semibold mb-2 sm:mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
        {isEditing ? (
          field === 'description' ? (
            <textarea
              value={value}
              onChange={(e) => setEditedSOP({
                ...editedSOP,
                [field]: e.target.value
              })}
              rows={4}
              className={`w-full resize-y border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'} focus:outline-none focus:ring-2 focus:ring-red-500`}
              placeholder={`Enter ${title.toLowerCase()}...`}
            />
          ) : (
            <input
              type="text"
              value={value}
              onChange={(e) => setEditedSOP({
                ...editedSOP,
                [field]: e.target.value
              })}
              className={`w-full border rounded px-2 py-1 text-xs sm:text-sm ${darkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white border-gray-300'}`}
            />
          )
        ) : (
          <p className={`text-sm sm:text-base ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed break-words`}>{value}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen">
      <StartSidebar />
      <div className={`min-h-screen p-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-lg p-6">
            <div className="flex items-center justify-between mb-8">
              <BackButton onClick={() => navigate('/start/operations')} />
              <div className="flex items-center">
                <Book className={`text-4xl mr-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Standard Operating Procedures</h1>
              </div>
              <div className="w-[120px] flex justify-end">
                <AddButton onClick={handleNewTemplate} />
              </div>
            </div>

            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6`}>
            {/* Tabs */}
            <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} mb-8`}>
              <nav className="-mb-px flex justify-center space-x-8" aria-label="Tabs">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                        ${activeTab === tab.id
                          ? 'border-red-500 text-red-600'
                          : `${darkMode ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                        }
                      `}
                    >
                      <div className="flex items-center">
                        <Icon className="w-4 h-4 mr-2" />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* SOP Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {sops[activeTab] && sops[activeTab].length > 0 ? (
                sops[activeTab].map((sop, index) => {
                  // Get the display title - use customized title if available, otherwise template title
                  const displayTitle = userCustomizations[sop.id]?.title || sop.title;
                  const hasCustomizations = !!userCustomizations[sop.id];
                  
                  // Check if this SOP is unlocked
                  // If unlockedSOPs is null (not set), all SOPs are unlocked (backward compatibility)
                  // If unlockedSOPs is an array, only SOPs in that array are unlocked
                  const isUnlocked = unlockedSOPs === null || (Array.isArray(unlockedSOPs) && unlockedSOPs.includes(sop.id));
                  
                  return (
                    <div
                      key={sop.id || index}
                      className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} border rounded-lg p-6 transition-shadow duration-200 relative ${
                        isUnlocked 
                          ? 'hover:bg-gray-50 dark:hover:bg-gray-600 hover:shadow-md cursor-pointer' 
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                      onClick={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Add a small delay to ensure the click is processed
                        setTimeout(() => handleSOPClick(sop, event), 0);
                      }}
                      onMouseDown={(event) => {
                        if (!isUnlocked) {
                          event.preventDefault();
                          event.stopPropagation();
                          return;
                        }
                        // Prevent default to avoid any interference
                        event.preventDefault();
                      }}
                    >
                      {/* Lock icon for locked SOPs */}
                      {!isUnlocked && (
                        <div className="absolute top-3 right-3">
                          <Lock className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                        </div>
                      )}
                    <div className="flex flex-col items-center mb-4">
                      <h3 className={`text-lg font-semibold text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>{displayTitle}</h3>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {hasCustomizations ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                          }`}>
                            Tailored
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                          }`}>
                            Template
                          </span>
                        )}
                      </div>
                    </div>
                    <button 
                      className={`w-full py-1.5 px-3 rounded-md transition-colors duration-200 text-sm ${
                        isUnlocked
                          ? darkMode 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : 'bg-red-600 text-white hover:bg-red-700'
                          : darkMode
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                      disabled={!isUnlocked}
                    >
                      {isUnlocked ? 'View Details' : 'Locked'}
                    </button>
                  </div>
                  );
                })
              ) : (
                <div className={`col-span-full text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <p className="text-lg">No SOPs found for this department.</p>
                  <p className="text-sm mt-2">Create SOPs in the Templates section first.</p>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && selectedSOP && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div ref={modalRef} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg w-full max-w-[95vw] sm:max-w-[90vw] md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto`}>
            <div className="p-4 sm:p-6 md:p-8">
              {(() => {
                try {
                  return (
                    <>
                      <div className="flex flex-col items-center mb-6 sm:mb-8">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editedSOP.title}
                            onChange={(e) => setEditedSOP({ ...editedSOP, title: e.target.value })}
                            className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center border rounded px-2 py-1 w-full ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
                          />
                        ) : (
                          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-2 text-center px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedSOP.title}</h2>
                        )}
                      </div>

                      <div className="space-y-4 sm:space-y-6">
                {/* Attachment-only template: show attachment with View/Download */}
                {((isEditing ? editedSOP : selectedSOP)?.attachmentOnly && (isEditing ? editedSOP : selectedSOP)?.attachment) ? (
                  <div className={`${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} rounded-lg p-4 sm:p-6 border`}>
                    <h3 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Attachment</h3>
                    <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {(isEditing ? editedSOP : selectedSOP).attachment.name}
                    </p>
                    <div className="flex gap-3">
                      <a
                        href={(isEditing ? editedSOP : selectedSOP).attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        View
                      </a>
                      <a
                        href={(isEditing ? editedSOP : selectedSOP).attachment.url}
                        download={(isEditing ? editedSOP : selectedSOP).attachment.name}
                        className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      >
                        Download
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    {renderEditableField('description', 'Description')}
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className={`text-lg sm:text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Steps</h3>
                      </div>
                      {(isEditing ? (editedSOP.steps || []) : (selectedSOP.steps || [])).map((step, index) => (
                        <div key={index}>
                          {renderStep(isEditing ? (editedSOP.steps || [])[index] : step, index)}
                        </div>
                      ))}
                      {isEditing && (
                        <div className="flex justify-center pt-3 sm:pt-4">
                          <button
                            onClick={handleAddStep}
                            className="text-red-600 hover:text-red-700 flex items-center text-xs sm:text-sm border border-red-600 rounded px-3 sm:px-4 py-2 hover:bg-red-50"
                          >
                            <Plus className="w-4 h-4 mr-1" /> Add Step
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

                             {/* Action Buttons */}
               <div className={`mt-6 sm:mt-8 pt-4 sm:pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-0`}>
                 <div className="flex flex-wrap gap-2 sm:space-x-4">
                   {selectedSOP.hasCustomizations && !isNewTemplate && (
                     <button
                       onClick={handleResetToTemplate}
                       className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                     >
                       Reset to Template
                     </button>
                   )}
                   {isNewTemplate && (
                     <button
                       onClick={handleDeleteNewTemplate}
                       className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                     >
                       <Trash2 className="w-4 h-4 mr-2" />
                       Delete
                     </button>
                   )}
                 </div>
                 <div className="flex flex-wrap gap-2 sm:space-x-4 justify-end">
                   {isNewTemplate ? (
                     <>
                       <button
                         onClick={handleCloseNewTemplate}
                         className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                       >
                         Close
                       </button>
                       <SaveButton onClick={handleSave} />
                     </>
                   ) : (
                     <>
                       {!isNewTemplate && (
                         <button
                           onClick={() => setIsModalOpen(false)}
                           className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                         >
                           Close
                         </button>
                       )}
                       {isEditing ? (
                         <>
                           <button
                             onClick={handleCancel}
                             className={`px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
                           >
                             Cancel
                           </button>
                           <SaveButton onClick={handleSave} />
                         </>
                       ) : (
                         <>
                           <EditButton onClick={handleEdit} />
                           {selectedSOP?.attachmentOnly && selectedSOP?.attachment ? (
                             <a
                               href={selectedSOP.attachment.url}
                               download={selectedSOP.attachment.name}
                               className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                             >
                               <Download className="w-4 h-4 mr-2" />
                               <span className="hidden sm:inline">Download</span>
                               <span className="sm:hidden">Download</span>
                             </a>
                           ) : (
                             <button
                               onClick={handleDownloadPDF}
                               className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
                             >
                               <Download className="w-4 h-4 mr-2" />
                               <span className="hidden sm:inline">Download PDF</span>
                               <span className="sm:hidden">PDF</span>
                             </button>
                           )}
                         </>
                       )}
                     </>
                   )}
                 </div>
                      </div>
                    </>
                  );
                } catch (error) {
                  console.error('Error rendering SOP modal:', error);
                  return (
                    <div className="text-center py-8">
                      <h2 className="text-xl font-semibold text-red-600 mb-4">Error Loading SOP</h2>
                      <p className="text-gray-600 mb-4">There was an error loading this SOP. Please try again.</p>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Close
                      </button>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartSOPs;

